import { type ReactNode } from 'react'
import { ClassNameValue } from 'tailwind-merge'
import clsx from 'clsx'
import { Button } from '@dgshahr/ui-kit'
import IconArrowRight2Icon from '@/components/common/icons/IconArrowRight2'
import IconArrowLeft2Icon from '@/components/common/icons/IconArrowLeft2'
import { useButtonActionsContext } from '@/utils/contexts/form-layout/ButtonActionsContext'

interface IFormLayoutProps {
  children: ReactNode
}

function FormLayout({ children }: Readonly<IFormLayoutProps>) {
  return (
    <div className="h-full w-full desktop:p-16 desktop:!pt-8 bg-gray-100">
      {children}
    </div>
  )
}

interface IHeaderProps {
  children: ReactNode
  className?: ClassNameValue
}

function Header({ children, className }: Readonly<IHeaderProps>) {
  return (
    <header
      className={clsx(
        'h-28 border-b border-gray-200 py-4 bg-white desktop:border desktop:rounded-2xl desktop:mb-4',
        className
      )}
    >
      {children}
    </header>
  )
}

interface IMainProps {
  children: ReactNode
  className?: ClassNameValue
}

function Main({ children, className }: Readonly<IMainProps>) {
  const { buttonActions } = useButtonActionsContext()
  const {
    nextStepAction,
    prevStepAction,
    nextStepDisabled,
    prevStepDisabled,
    nextStepIsLoading,
    prevStepIsLoading,
    customButtons,
    nextStepLabel,
    prevStepLabel
  } = buttonActions

  return (
    <main
      className={clsx(
        'p-4 pt-0 grow flex !h-[calc(100%_-_112px)] desktop:h-[calc(100%_-_130px)] flex-col desktop:border border-gray-200 desktop:rounded-2xl bg-white',
        className
      )}
    >
      <section className="h-full overflow-y-auto no-scrollbar shrink">
        {children}
      </section>
      <section className="h-11 w-full mt-3 shrink-0 flex gap-4 items-center justify-end">
        {customButtons ? (
          customButtons
        ) : (
          <>
            <Button
              disabled={prevStepDisabled}
              onClick={prevStepAction}
              isLoading={prevStepIsLoading}
              rightIcon={<IconArrowRight2Icon />}
              variant={'outline'}
              className="grow !h-full flex justify-center items-center desktop:h-fit desktop:grow-0"
            >
              {prevStepLabel ?? 'مرحله قبلی'}
            </Button>
            <Button
              type="submit"
              disabled={nextStepDisabled}
              onClick={nextStepAction}
              isLoading={nextStepIsLoading}
              leftIcon={<IconArrowLeft2Icon />}
              variant={'primary'}
              className="grow !h-full flex justify-center items-center desktop:h-fit desktop:grow-0"
            >
              {nextStepLabel ?? 'مرحله بعدی'}
            </Button>
          </>
        )}
      </section>
    </main>
  )
}

FormLayout.Header = Header
FormLayout.Main = Main

export default FormLayout
